import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import {
  updateProject,
  getUsedInOptions, getUsedInOptionUsage,
  createUsedInOption, updateUsedInOption,
  mergeUsedInOption, deleteUsedInOption,
} from '../utils/api';
import styles from './ProjectSettings.module.css';

// Project Settings page. Currently exposes one knob per the 2026-04-26
// drawings overhaul: the default expiration (in days) for newly-generated
// public share links. Saved on blur or via the Save button.

export default function ProjectSettings() {
  const { project, setProject } = useStore();
  // Read role once — calling useStore twice inside a `||` short-circuits
  // when the first call returns admin, which violates rules-of-hooks
  // (the second hook only runs sometimes). Render's CI=true treats
  // that ESLint error as a build failure.
  const role = useStore((s) => s.role);
  const canEdit = role === 'admin' || role === 'pm';

  const [shareDays, setShareDays] = useState(project?.share_link_default_days ?? 30);
  const [saving, setSaving]       = useState(false);
  const [dirty, setDirty]         = useState(false);

  useEffect(() => {
    setShareDays(project?.share_link_default_days ?? 30);
    setDirty(false);
  }, [project?.id, project?.share_link_default_days]);

  async function save() {
    if (!project) return;
    if (!Number.isFinite(shareDays) || shareDays < 1 || shareDays > 365) {
      toast.error('Share link days must be between 1 and 365');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateProject(project.id, { share_link_default_days: shareDays });
      setProject(updated);
      setDirty(false);
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!project) return null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Project Settings</h1>
        <span className={styles.subtitle}>{project.name}{project.number ? ` · ${project.number}` : ''}</span>
      </header>

      <div className={styles.body}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Sharing</h2>
            <p className={styles.sectionDesc}>
              Default expiration for new public share links generated from
              the Drawings page. Each link can override this default at
              creation time.
            </p>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Default share link duration</label>
            <div className={styles.fieldControl}>
              <input
                type="number"
                min="1"
                max="365"
                step="1"
                className={styles.numberInput}
                value={shareDays}
                disabled={!canEdit || saving}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setShareDays(Number.isFinite(v) ? v : 30);
                  setDirty(true);
                }}
              />
              <span className={styles.fieldUnit}>days</span>
              {canEdit && (
                <button
                  className={styles.saveBtn}
                  onClick={save}
                  disabled={!dirty || saving}
                >{saving ? 'Saving…' : 'Save'}</button>
              )}
            </div>
            <div className={styles.fieldHint}>
              Min 1 day, max 365 days. Existing share links keep their
              original expiration — this only affects newly-generated links.
            </div>
          </div>
        </section>

        <UsedInOptionsSection canEdit={canEdit} project={project} />
      </div>
    </div>
  );
}

// ── Used-In options manager ─────────────────────────────────────────────
// Drives the Used In dropdown on the concrete-mixes page. PM/Admin only.
//
// New behaviour (2026-04-29): renames cascade into mixes + submissions
// on the server, so fixing a typo here actually fixes every existing
// row that was tagged with the old name. There's also a Merge action
// for "these two are the same thing — collapse them" — implemented as
// a one-shot rename + dedupe + delete on the server.
function UsedInOptionsSection({ canEdit, project }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding]   = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameUsage, setRenameUsage] = useState(null); // { mixes, submissions }
  const [mergingFrom, setMergingFrom] = useState(null); // option being merged FROM

  async function refresh() {
    if (!project) return;
    setLoading(true);
    try { setOptions(await getUsedInOptions(project.id)); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [project?.id]);

  async function handleAdd() {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await createUsedInOption({ project_id: project.id, name: newName.trim() });
      setNewName('');
      await refresh();
    } catch (err) {
      toast.error(err.message);
    } finally { setAdding(false); }
  }

  // Begin renaming: pre-fetch usage so the confirm dialog can show
  // "this will update N mix rows and M daily reports".
  async function startRename(opt) {
    setRenamingId(opt.id);
    setRenameValue(opt.name);
    setRenameUsage(null);
    try { setRenameUsage(await getUsedInOptionUsage(opt.id)); }
    catch (_) { /* non-fatal — fall back to no preview */ }
  }
  function cancelRename() {
    setRenamingId(null);
    setRenameValue('');
    setRenameUsage(null);
  }
  async function handleRename(opt) {
    const newVal = renameValue.trim();
    if (!newVal || newVal === opt.name) { cancelRename(); return; }
    // Cascade is heavyweight enough to warrant a confirmation when the
    // option is in use. We already have the count from startRename.
    const total = (renameUsage?.mixes ?? 0) + (renameUsage?.submissions ?? 0);
    if (total > 0) {
      const ok = window.confirm(
        `Rename "${opt.name}" to "${newVal}"?\n\n` +
        `This will update ${renameUsage.mixes} mix${renameUsage.mixes === 1 ? '' : 'es'} ` +
        `and ${renameUsage.submissions} daily report${renameUsage.submissions === 1 ? '' : 's'} ` +
        `that reference the old name.`
      );
      if (!ok) return;
    }
    try {
      await updateUsedInOption(opt.id, { name: newVal });
      cancelRename();
      await refresh();
      toast.success(`Renamed to "${newVal}"`);
    } catch (err) { toast.error(err.message); }
  }

  async function handleDelete(opt) {
    // Pull usage so we can warn the user if they're about to leave
    // dangling references — and steer them toward Merge if the option
    // is in use.
    let usage = null;
    try { usage = await getUsedInOptionUsage(opt.id); } catch (_) {}
    const refs = (usage?.mixes ?? 0) + (usage?.submissions ?? 0);
    if (refs > 0) {
      const proceed = window.confirm(
        `"${opt.name}" is referenced by ${usage.mixes} mix${usage.mixes === 1 ? '' : 'es'} ` +
        `and ${usage.submissions} daily report${usage.submissions === 1 ? '' : 's'}.\n\n` +
        `Deleting only removes the option from the dropdown — existing ` +
        `references will remain as free-text. To re-tag those rows to ` +
        `another option, use Merge instead.\n\nDelete anyway?`
      );
      if (!proceed) return;
    } else {
      if (!window.confirm(`Delete "${opt.name}"?`)) return;
    }
    try {
      await deleteUsedInOption(opt.id);
      await refresh();
    } catch (err) { toast.error(err.message); }
  }

  function startMerge(opt) { setMergingFrom(opt); }

  async function performMerge(toId) {
    if (!mergingFrom || !toId || toId === mergingFrom.id) return;
    const target = options.find((o) => o.id === toId);
    if (!target) return;
    let usage = null;
    try { usage = await getUsedInOptionUsage(mergingFrom.id); } catch (_) {}
    const refs = (usage?.mixes ?? 0) + (usage?.submissions ?? 0);
    const ok = window.confirm(
      `Merge "${mergingFrom.name}" into "${target.name}"?\n\n` +
      `Every existing reference to "${mergingFrom.name}" will be re-tagged ` +
      `as "${target.name}"` +
      (refs > 0
        ? ` (${usage.mixes} mix${usage.mixes === 1 ? '' : 'es'}, ` +
          `${usage.submissions} daily report${usage.submissions === 1 ? '' : 's'})`
        : '') +
      `, and "${mergingFrom.name}" will be removed from the option list. ` +
      `This can't be undone.`
    );
    if (!ok) return;
    try {
      await mergeUsedInOption(mergingFrom.id, target.id);
      setMergingFrom(null);
      await refresh();
      toast.success(`Merged into "${target.name}"`);
    } catch (err) { toast.error(err.message); }
  }

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Used In</h2>
        <p className={styles.sectionDesc}>
          Concrete elements on project. Used as the dropdown for the Used In
          column on the concrete-mixes table. New options are added
          automatically when you create or bulk-import mixes — edit or
          merge them here to fix typos and tidy the list. Renames cascade
          into existing mixes and daily reports.
        </p>
      </div>

      {canEdit && (
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Add option</label>
          <div className={styles.fieldControl}>
            <input
              className={styles.numberInput}
              style={{ width: '260px' }}
              placeholder="e.g. Foundations"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              disabled={adding}
            />
            <button
              className={styles.saveBtn}
              onClick={handleAdd}
              disabled={adding || !newName.trim()}
            >{adding ? '…' : 'Add'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className={styles.fieldHint}>Loading…</div>
      ) : options.length === 0 ? (
        <div className={styles.fieldHint}>
          No options yet — they'll be added automatically when you create
          your first mix.
        </div>
      ) : (
        <ul className={styles.usedInList}>
          {options.map((o) => (
            <li key={o.id} className={styles.usedInRow}>
              {renamingId === o.id ? (
                <>
                  <input
                    className={styles.numberInput}
                    style={{ width: '260px' }}
                    value={renameValue}
                    autoFocus
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')  { e.preventDefault(); handleRename(o); }
                      if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                    }}
                  />
                  <button className={styles.saveBtn} onClick={() => handleRename(o)}>Save</button>
                  <button className={styles.usedInBtn} onClick={cancelRename}>Cancel</button>
                </>
              ) : mergingFrom?.id === o.id ? (
                <>
                  <span className={styles.usedInName}>
                    Merge "{o.name}" into…
                  </span>
                  <select
                    className={styles.numberInput}
                    style={{ width: '220px' }}
                    autoFocus
                    defaultValue=""
                    onChange={(e) => performMerge(e.target.value)}
                  >
                    <option value="" disabled>Pick target option…</option>
                    {options
                      .filter((x) => x.id !== o.id)
                      .map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                  </select>
                  <button className={styles.usedInBtn} onClick={() => setMergingFrom(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className={styles.usedInName}>{o.name}</span>
                  {canEdit && (
                    <span className={styles.usedInBtns}>
                      <button
                        className={styles.usedInBtn}
                        onClick={() => startRename(o)}
                      >Rename</button>
                      <button
                        className={styles.usedInBtn}
                        onClick={() => startMerge(o)}
                        disabled={options.length < 2}
                        title={options.length < 2 ? 'Need another option to merge into' : ''}
                      >Merge</button>
                      <button
                        className={`${styles.usedInBtn} ${styles.usedInBtnDanger}`}
                        onClick={() => handleDelete(o)}
                      >Delete</button>
                    </span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
